import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Browser } from '@capacitor/browser';
import { CreditCard, Lock, Check } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { Section } from '@shared/ui/containers/Section';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { ListItem } from '@shared/ui/lists/ListItem';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Button } from '@shared/ui/primitives/Button';
import { toast } from '@shared/ui/feedback/Toast';
import { platform } from '@shared/lib/platform';
import { env } from '@config/env';
import { fetchPaymentMethods, startCheckout } from './api';
import { fetchCart } from '@features/cart/api';
import { formatMoney } from '@shared/lib/format';

/**
 * Checkout — pick a payment method, then either finalize inline or
 * redirect via @capacitor/browser (native) / window.location (web).
 *
 * The provider's redirect lands back at `/checkout/payment/redirect`;
 * deep-link handlers route the user to `/checkout/success` once the
 * webhook confirms the payment.
 */
export function Checkout(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const inApp = location.pathname.startsWith('/app/');
  const cartRoute = inApp ? '/app/cart' : '/cart';

  const cartQuery = useQuery({ queryKey: ['cart'], queryFn: fetchCart });
  const methodsQuery = useQuery({ queryKey: ['payment-methods'], queryFn: fetchPaymentMethods });

  const checkoutMutation = useMutation({
    mutationFn: startCheckout,
    onSuccess: async (session) => {
      if (session.redirect_url) {
        if (platform.isNative()) {
          await Browser.open({ url: session.redirect_url, presentationStyle: 'popover' });
          // Native flow returns via deep-link handler in Phase E; for now
          // prompt the user to confirm completion.
        } else {
          window.location.href = session.redirect_url;
          return;
        }
      } else if (session.order_id) {
        navigate(`/checkout/success?order=${session.order_id}`);
      }
    },
    onError: (e) => toast.danger("Couldn't start checkout", e instanceof Error ? e.message : undefined),
  });

  if (cartQuery.isLoading || methodsQuery.isLoading) {
    return <Page><Skeleton className="h-32" /></Page>;
  }
  if (cartQuery.isError || methodsQuery.isError) {
    return <Page><ErrorState onRetry={() => { cartQuery.refetch(); methodsQuery.refetch(); }} /></Page>;
  }

  const cart = cartQuery.data;
  const methods = methodsQuery.data ?? [];

  if (!cart || cart.items.length === 0) {
    navigate(cartRoute, { replace: true });
    return <></>;
  }

  return (
    <Page>
      <header className="mt-2">
        <h1 className="font-display text-display-xl text-ink leading-tight">Checkout.</h1>
        <p className="text-body text-ink-secondary mt-1">
          {cart.items.length} item{cart.items.length === 1 ? '' : 's'} · {formatMoney(cart.total, { currency: cart.currency })}
        </p>
      </header>

      <Section title="Payment method">
        <ListGroup>
          {methods.map((m) => (
            <ListItem
              key={m.method_id}
              title={m.label}
              subtitle={m.description}
              leading={<CreditCard className="h-5 w-5 text-ink-muted" strokeWidth={1.5} />}
              trailing={selectedMethod === m.method_id ? <Check className="h-4 w-4 text-primary-600" strokeWidth={2} /> : undefined}
              active={selectedMethod === m.method_id}
              onClick={() => setSelectedMethod(m.method_id)}
            />
          ))}
        </ListGroup>
      </Section>

      <Card className="mt-6">
        <p className="text-caption text-ink-muted flex items-center gap-1">
          <Lock className="h-3 w-3" strokeWidth={1.5} />
          Payment is processed by your community's provider — we never see your card details.
        </p>
      </Card>

      <Button
        size="lg"
        width="full"
        className="mt-6"
        disabled={!selectedMethod}
        loading={checkoutMutation.isPending}
        onClick={() => {
          if (!selectedMethod) return;
          const returnUrl = `${env.portalBaseUrl}/checkout/payment/redirect`;
          checkoutMutation.mutate({
            method_id: selectedMethod,
            return_url: returnUrl,
            items: cart.items.map((item) => ({
              product_id: item.product_id,
              variant_id: item.variant_id,
              quantity: item.quantity,
            })),
          });
        }}
      >
        Pay {formatMoney(cart.total, { currency: cart.currency })}
      </Button>
    </Page>
  );
}
